import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  MessageSquare,
  Mail,
  X,
  Phone,
  ChevronDown,
} from 'lucide-react'
import WhatsAppTemplateSelector from '@/components/WhatsAppTemplateSelector'
import { ChatEditorRef } from '@/components/editor/ChatEditor'
import type { Macro } from '@/components/MacroPicker'
import MessageComposer from './MessageComposer'
import { getInitials, getAvatarColor, getCsrfToken } from './utils'
import type { Agent, ComposeUser, EndUser, PendingAttachment, WhatsAppTemplate, WhatsAppConfiguration } from './types'

interface ComposePanelProps {
  currentAgent: Agent
  filter: string
  statusFilter: string
  buildFilterUrl: (
    conversationId?: number | null,
    assignmentFilter?: string,
    status?: string,
    teamId?: string | null,
    channel?: string | null
  ) => string
  onCancel: () => void
  whatsappTemplates: WhatsAppTemplate[]
  whatsappConfigurations: WhatsAppConfiguration[]
  macros: Macro[]
}

interface WhatsAppTemplatePayload {
  id: number
  variables: string[]
}

export default function ComposePanel({
  currentAgent,
  filter,
  statusFilter,
  buildFilterUrl,
  onCancel,
  whatsappTemplates,
  whatsappConfigurations,
  macros,
}: ComposePanelProps) {
  const { organizationName } = usePage<{ organizationName: string | null }>().props
  const [composeChannel, setComposeChannel] = useState<'chat' | 'email' | 'whatsapp'>('chat')
  const [composeToQuery, setComposeToQuery] = useState('')
  const [composeSelectedUser, setComposeSelectedUser] = useState<ComposeUser | null>(null)
  const [composeResults, setComposeResults] = useState<ComposeUser[]>([])
  const [composeSearchOpen, setComposeSearchOpen] = useState(false)
  const [composeActiveIndex, setComposeActiveIndex] = useState(-1)
  const [composeSearchLoading, setComposeSearchLoading] = useState(false)
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const composePlainTextRef = useRef('')
  const [composeHasText, setComposeHasText] = useState(false)
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const composeEditorRef = useRef<ChatEditorRef>(null)
  const [isCreatingConversation, setIsCreatingConversation] = useState(false)

  // WhatsApp-specific state
  const [selectedWhatsappConfigId, setSelectedWhatsappConfigId] = useState<number | null>(
    () => {
      if (whatsappConfigurations.length === 1) return whatsappConfigurations[0].id
      const defaultConfig = whatsappConfigurations.find(c => c.isDefault)
      return defaultConfig?.id ?? null
    }
  )

  const hasWhatsappConfigs = whatsappConfigurations.length > 0

  function formatConfigLabel(config: WhatsAppConfiguration): string {
    if (config.name) return `${config.name} (${config.displayPhoneNumber})`
    return config.displayPhoneNumber || ''
  }

  // Computed values
  const composeDisplayValue = useMemo(() => {
    if (!composeSelectedUser) return composeToQuery

    if (composeChannel === 'whatsapp') {
      const phone = composeSelectedUser.phone || composeSelectedUser.whatsappWaId || ''
      const name = composeSelectedUser.name
      if (name && phone) return `${name} (${phone})`
      return name || phone
    }

    const { name, email } = composeSelectedUser
    if (name && email) return `${name} <${email}>`
    return name || email || ''
  }, [composeSelectedUser, composeToQuery, composeChannel])

  const composeActiveDescendantId =
    !composeSelectedUser && composeSearchOpen && composeActiveIndex >= 0 && composeResults[composeActiveIndex]
      ? `compose-to-option-${composeResults[composeActiveIndex].id}`
      : undefined

  const canSend = useMemo(() => {
    if (!composeSelectedUser || isCreatingConversation) return false

    const hasContent = composeHasText || pendingAttachments.length > 0

    switch (composeChannel) {
      case 'email':
        return !!composeSelectedUser.email && hasContent
      case 'whatsapp': {
        const hasPhone = !!(composeSelectedUser.phone || composeSelectedUser.whatsappWaId)
        return hasPhone && !!selectedWhatsappConfigId
      }
      default:
        return hasContent
    }
  }, [
    composeSelectedUser, isCreatingConversation, composeChannel, composeHasText,
    pendingAttachments, selectedWhatsappConfigId,
  ])

  const handleComposeTextChange = useCallback((text: string) => {
    composePlainTextRef.current = text
    const nextHasText = text.trim().length > 0
    setComposeHasText((prev) => (prev === nextHasText ? prev : nextHasText))
  }, [])

  // Channel switch: clear incompatible state
  useEffect(() => {
    if (composeChannel === 'email') {
      if (composeSelectedUser && !composeSelectedUser.email) {
        const fallbackQuery = composeSelectedUser.name || ''
        setComposeSelectedUser(null)
        setComposeToQuery(fallbackQuery)
      }
      setComposeResults((prev) => prev.filter((user) => !!user.email))
    } else if (composeChannel === 'whatsapp') {
      if (composeSelectedUser && !composeSelectedUser.phone && !composeSelectedUser.whatsappWaId) {
        const fallbackQuery = composeSelectedUser.name || ''
        setComposeSelectedUser(null)
        setComposeToQuery(fallbackQuery)
      }
      setComposeResults((prev) => prev.filter((user) => !!user.phone || !!user.whatsappWaId))
    }
  }, [composeChannel, composeSelectedUser])

  // Contact search
  useEffect(() => {
    if (composeSelectedUser) return
    const query = composeToQuery.trim()
    if (query.length < 2) {
      setComposeResults([])
      setComposeSearchOpen(false)
      setComposeActiveIndex(-1)
      setComposeSearchLoading(false)
      return
    }

    let isActive = true
    const timeout = window.setTimeout(async () => {
      setComposeSearchLoading(true)
      try {
        const response = await fetch(`/contacts/search?q=${encodeURIComponent(query)}`)
        if (!response.ok) throw new Error('Erro ao buscar contatos')
        const data = await response.json()
        let users = (data?.users || []) as ComposeUser[]
        if (composeChannel === 'email') users = users.filter((user) => !!user.email)
        if (composeChannel === 'whatsapp') users = users.filter((user) => !!user.phone || !!user.whatsappWaId)
        if (!isActive) return
        setComposeResults(users)
        setComposeSearchOpen(true)
      } catch (error) {
        if (!isActive) return
        console.error('Error searching contacts:', error)
        setComposeResults([])
        setComposeSearchOpen(true)
      } finally {
        if (isActive) setComposeSearchLoading(false)
      }
    }, 250)

    return () => {
      isActive = false
      clearTimeout(timeout)
    }
  }, [composeToQuery, composeChannel, composeSelectedUser])

  // Reset active index when results change
  useEffect(() => {
    if (!composeSearchOpen || composeSelectedUser) {
      setComposeActiveIndex(-1)
      return
    }
    setComposeActiveIndex((prev) => {
      if (composeResults.length === 0) return -1
      if (prev >= 0 && prev < composeResults.length) return prev
      return 0
    })
  }, [composeResults, composeSearchOpen, composeSelectedUser])

  // Scroll active item into view
  useEffect(() => {
    if (!composeSearchOpen || composeActiveIndex < 0) return
    const user = composeResults[composeActiveIndex]
    if (!user) return
    document.getElementById(`compose-to-option-${user.id}`)?.scrollIntoView({ block: 'nearest' })
  }, [composeActiveIndex, composeResults, composeSearchOpen])

  // Create conversation handler
  async function handleCreateConversation(
    e?: FormEvent,
    whatsappTemplate?: WhatsAppTemplatePayload,
  ): Promise<boolean> {
    e?.preventDefault()
    if (isCreatingConversation) return false

    if (!composeSelectedUser) {
      toast.error('Selecione um destinatário')
      return false
    }
    if (composeChannel === 'email' && !composeSelectedUser.email) {
      toast.error('Contato sem e-mail')
      return false
    }
    if (composeChannel === 'whatsapp') {
      if (!composeSelectedUser.phone && !composeSelectedUser.whatsappWaId) {
        toast.error('Contato sem telefone')
        return false
      }
      if (!selectedWhatsappConfigId) {
        toast.error('Selecione o número remetente')
        return false
      }
      if (!whatsappTemplate) {
        toast.error('Selecione um template')
        return false
      }
    }

    if (composeChannel !== 'whatsapp') {
      const hasTextContent = composePlainTextRef.current.trim().length > 0
      if (!hasTextContent && pendingAttachments.length === 0) {
        toast.error('A mensagem não pode ficar vazia')
        return false
      }
    }

    setIsCreatingConversation(true)

    try {
      const formData = new FormData()
      formData.append('conversation[end_user_id]', composeSelectedUser.id.toString())
      formData.append('conversation[channel]', composeChannel)

      if (composeChannel === 'whatsapp') {
        formData.append('conversation[whatsapp_configuration_id]', selectedWhatsappConfigId!.toString())
        formData.append('message[whatsapp_template_id]', whatsappTemplate!.id.toString())
        whatsappTemplate!.variables.forEach((variable) => {
          formData.append('message[whatsapp_template_variables][]', variable)
        })
      } else {
        formData.append('message[body]', composeBody)
        if (composeChannel === 'email') {
          formData.append('message[email_subject]', composeSubject)
        }
        pendingAttachments.forEach((attachment) => {
          formData.append('message[attachments][]', attachment.file)
        })
      }

      const response = await fetch('/conversations', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken() },
        body: formData,
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data?.error || data?.errors?.join(', ') || 'Erro ao criar conversa')
      }
      if (!data?.conversation_id) {
        throw new Error('Resposta inválida ao criar conversa')
      }

      onCancel()
      router.visit(buildFilterUrl(data.conversation_id, filter, statusFilter), {
        preserveState: false,
      })
      return true
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar conversa')
      return false
    } finally {
      setIsCreatingConversation(false)
    }
  }

  // Build a minimal EndUser from the selected ComposeUser for macro interpolation
  function composeUserToEndUser(user: ComposeUser): EndUser {
    return {
      id: user.id,
      name: user.name || '',
      email: user.email,
      company: user.companyName || null,
      company_id: null,
      phone: user.phone,
      locale: null,
      location: null,
      first_seen_at: null,
      last_seen_at: null,
      signed_up_at: null,
      web_sessions_count: 0,
      browser: null,
      os: null,
      browser_language: null,
      last_contacted_at: null,
      last_heard_from_at: null,
      messages_count: 0,
      conversations_count: 0,
      current_page_url: null,
      current_page_title: null,
      recent_page_views: [],
      recent_conversations: [],
      contact_notes: [],
    }
  }

  const isWhatsapp = composeChannel === 'whatsapp'

  const cancelButton = (
    <button
      type="button"
      onClick={onCancel}
      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
    >
      Cancelar
    </button>
  )

  return (
    <div className="flex-1 flex flex-col bg-white">
      <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900">Nova conversa</h2>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col justify-end px-4 py-6">
          <div className="max-w-3xl mx-auto w-full space-y-4">
            {/* Channel selector */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setComposeChannel('chat')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                  composeChannel === 'chat'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Chat
              </button>
              <button
                type="button"
                onClick={() => setComposeChannel('email')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                  composeChannel === 'email'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                Email
              </button>
              {hasWhatsappConfigs && (
                <button
                  type="button"
                  onClick={() => setComposeChannel('whatsapp')}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                    composeChannel === 'whatsapp'
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Phone className="w-3.5 h-3.5" />
                  WhatsApp
                </button>
              )}
            </div>

            <div className="border border-gray-200 rounded-2xl bg-white">
              {/* From field */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                <span className="text-sm text-gray-500 w-16">De</span>
                {isWhatsapp && whatsappConfigurations.length === 1 && (
                  <span className="text-sm font-medium text-gray-900">
                    {formatConfigLabel(whatsappConfigurations[0])}
                  </span>
                )}
                {isWhatsapp && whatsappConfigurations.length > 1 && (
                  <div className="relative flex-1">
                    <select
                      value={selectedWhatsappConfigId ?? ''}
                      onChange={(e) => setSelectedWhatsappConfigId(e.target.value ? Number(e.target.value) : null)}
                      className="w-full text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none"
                    >
                      <option value="">Selecione o número</option>
                      {whatsappConfigurations.map((config) => (
                        <option key={config.id} value={config.id}>
                          {formatConfigLabel(config)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                )}
                {!isWhatsapp && (
                  <div className="flex items-center gap-2">
                    {currentAgent.avatar_url ? (
                      <img
                        src={currentAgent.avatar_url}
                        alt={currentAgent.name}
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(currentAgent.name)}`}>
                        {getInitials(currentAgent.name)}
                      </div>
                    )}
                    <span className="text-sm font-medium text-gray-900">{currentAgent.name}</span>
                  </div>
                )}
              </div>

              {/* To field */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                <span className="text-sm text-gray-500 w-16">Para</span>
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={composeDisplayValue}
                    role="combobox"
                    aria-expanded={composeSearchOpen && !composeSelectedUser}
                    aria-controls="compose-to-listbox"
                    aria-activedescendant={composeActiveDescendantId}
                    onChange={(e) => {
                      if (composeSelectedUser) setComposeSelectedUser(null)
                      setComposeToQuery(e.target.value)
                      setComposeSearchOpen(true)
                    }}
                    onKeyDown={(e) => {
                      if (composeSelectedUser) return
                      if (!composeSearchOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                        setComposeSearchOpen(true)
                      }
                      const max = composeResults.length - 1
                      switch (e.key) {
                        case 'ArrowDown':
                          if (max < 0) return
                          e.preventDefault()
                          setComposeActiveIndex((prev) => prev < 0 ? 0 : Math.min(prev + 1, max))
                          break
                        case 'ArrowUp':
                          if (max < 0) return
                          e.preventDefault()
                          setComposeActiveIndex((prev) => prev < 0 ? max : Math.max(prev - 1, 0))
                          break
                        case 'Enter': {
                          if (!composeSearchOpen || composeSearchLoading || max < 0) return
                          e.preventDefault()
                          const index = composeActiveIndex >= 0 ? composeActiveIndex : 0
                          const user = composeResults[index]
                          if (!user) return
                          setComposeSelectedUser(user)
                          setComposeToQuery('')
                          setComposeSearchOpen(false)
                          setComposeActiveIndex(-1)
                          break
                        }
                        case 'Escape':
                          if (!composeSearchOpen) return
                          e.preventDefault()
                          setComposeSearchOpen(false)
                          setComposeActiveIndex(-1)
                          break
                      }
                    }}
                    onFocus={() => {
                      if (!composeSelectedUser && composeResults.length > 0) setComposeSearchOpen(true)
                    }}
                    onBlur={() => {
                      window.setTimeout(() => setComposeSearchOpen(false), 150)
                    }}
                    placeholder={isWhatsapp ? 'Buscar por nome ou telefone' : 'Buscar por nome ou e-mail'}
                    className={`w-full text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent pr-8 ${
                      isWhatsapp ? 'focus:ring-green-500' : 'focus:ring-blue-500'
                    }`}
                  />
                  {composeSelectedUser && (
                    <button
                      type="button"
                      onClick={() => {
                        setComposeSelectedUser(null)
                        setComposeToQuery('')
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {composeSearchOpen && !composeSelectedUser && (
                    <div
                      id="compose-to-listbox"
                      role="listbox"
                      className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto"
                    >
                      {composeSearchLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          Buscando contatos...
                        </div>
                      ) : composeResults.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          Nenhum contato encontrado
                        </div>
                      ) : (
                        composeResults.map((user, index) => {
                          const displayName = user.name || user.email || user.phone || 'Sem nome'
                          const isActive = index === composeActiveIndex
                          return (
                            <button
                              key={user.id}
                              id={`compose-to-option-${user.id}`}
                              type="button"
                              role="option"
                              aria-selected={isActive}
                              onMouseDown={(event) => event.preventDefault()}
                              onMouseEnter={() => setComposeActiveIndex(index)}
                              onClick={() => {
                                setComposeSelectedUser(user)
                                setComposeToQuery('')
                                setComposeSearchOpen(false)
                                setComposeActiveIndex(-1)
                              }}
                              className={`w-full text-left px-3 py-2 flex items-center gap-3 ${
                                isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(displayName)}`}>
                                {getInitials(displayName)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
                                {(user.email || user.phone) && (
                                  <div className="text-xs text-gray-500 truncate">
                                    {[user.email, user.phone].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Subject (email only) */}
              {composeChannel === 'email' && (
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                  <span className="text-sm text-gray-500 w-16">Assunto</span>
                  <input
                    type="text"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Assunto"
                    className="w-full text-sm text-gray-900 bg-transparent focus:outline-none"
                  />
                </div>
              )}

              {/* WhatsApp template selector and variables */}
              {isWhatsapp ? (
                <div className="px-4 py-3">
                  <WhatsAppTemplateSelector
                    templates={whatsappTemplates}
                    onCancel={onCancel}
                    isLoading={isCreatingConversation}
                    sendDisabled={!canSend}
                    onSend={(templateId, variables) =>
                      handleCreateConversation(undefined, { id: templateId, variables })
                    }
                  />
                </div>
              ) : (
                /* Message body (chat / email) */
                <div className="px-4 py-3">
                  <MessageComposer
                    onSubmit={handleCreateConversation}
                    onChange={setComposeBody}
                    onTextChange={handleComposeTextChange}
                    placeholder="Digite sua mensagem..."
                    disabled={isCreatingConversation}
                    channel={composeChannel}
                    macros={macros}
                    endUser={composeSelectedUser ? composeUserToEndUser(composeSelectedUser) : undefined}
                    currentAgent={currentAgent}
                    organizationName={organizationName}
                    pendingAttachments={pendingAttachments}
                    setPendingAttachments={setPendingAttachments}
                    editorRef={composeEditorRef}
                    extraActions={cancelButton}
                    sendDisabled={!canSend}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
